// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for merging location information in the Hardfin system.
 * Provides type-safe location record updates with Zod validation.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { locationSchema } from '@/location/api/types';
import { LocationComponents } from '@/sparse/api/types';

/**
 */
const mergeLocationResponseSchema = z
  .object({
    data: locationSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, location: data }));

/**
 */
type MergeLocationResponse = z.infer<typeof mergeLocationResponseSchema>;

/**
 */
export interface MergeLocationParameters {
  removedLocationId: string;
  name: string;
  isInventory: boolean;
  /** Sites only — a zone computes its customer from the parent site */
  customerId?: string | null;
  /** Sites only — a zone reads its address from the parent site */
  address?: LocationComponents | null;
  /** Sites only */
  isTransient?: boolean;
  /** Zones only */
  description?: string | null;
  /** Zones only */
  consignee?: string | null;
  /** Zones only — pins is_inventory instead of inheriting from the parent site */
  isInventoryOverride?: boolean;
}

/**

 */
export async function mergeLocation(
  key: string,
  locationType: string,
  parameters: MergeLocationParameters
): Promise<MergeLocationResponse> {
  const url = `/v1/location/${locationType}/${key}/merge`;
  const { address, ...rest } = parameters;
  const body = {
    ...rest,
    address: address
      ? {
          addressLine1: address.addressLine1,
          addressLine2: address.addressLine2,
          city: address.locality,
          state: address.administrativeArea,
          postalCode: address.postalCode,
          country: address.countryCode,
          formattedAddress: address.formattedAddress,
        }
      : address,
  };
  const response = await api.post(url, body);

  return parseResponse(mergeLocationResponseSchema, response.data, {
    schemaName: 'MergeLocation',
    endpoint: url,
  });
}
